import { api } from "./index";
import {
    AdminMarathonBetsFilters,
    AdminMarathonBetsResponse,
    AdminMarathonDetail,
    AdminMarathonDetailResponse,
    AdminMarathonFilters,
    AdminMarathonListResponse,
    AdminMarathonParticipantsFilters,
    AdminMarathonParticipantsResponse,
    AdminMarathonStatistics,
    AdminMarathonStatisticsResponse,
    AdminMarathonTicketsFilters,
    AdminMarathonTicketsResponse,
    AdminMarathonWinnersResponse,
    AdminMarathonUserAccountsAndTicketsResponse,
    AdminAwardableMarathonsResponse,
    AdminMarathonAwardPreviewResponse,
    AwardMarathonPrizePayload,
} from "../types/marathonAdmin";

const adminMarathonApi = api.injectEndpoints({
    endpoints: (builder) => ({
        getAdminMarathons: builder.query<
            AdminMarathonListResponse,
            AdminMarathonFilters | void
        >({
            query: (rawParams) => {
                const params = rawParams ?? {};

                return {
                    url: "/admin/marathons",
                    params: {
                        page: params.page,
                        per_page: params.per_page,
                        type: params.type,
                        status: params.status,
                        search: params.search,
                    },
                };
            },
            providesTags: (result) => [
                ...(result?.data?.map((item) => ({
                    type: "AdminMarathons" as const,
                    id: item.id,
                })) ?? []),
                { type: "AdminMarathons" as const, id: "LIST" },
            ],
        }),
        getAdminMarathon: builder.query<AdminMarathonDetail, number>({
            query: (id) => `/admin/marathons/${id}`,
            transformResponse: (response: AdminMarathonDetailResponse) =>
                response.data,
            providesTags: (_result, _error, id) => [
                { type: "AdminMarathons" as const, id },
            ],
        }),
        getAwardableMarathons: builder.query<AdminAwardableMarathonsResponse, void>({
            query: () => "/admin/marathons/awardable",
            providesTags: [{ type: "AdminMarathons" as const, id: "AWARDABLE" }],
        }),
        getAdminMarathonParticipants: builder.query<
            AdminMarathonParticipantsResponse,
            AdminMarathonParticipantsFilters
        >({
            query: ({ marathonId, ...rest }) => {
                const params = rest ?? {};

                return {
                    url: `/admin/marathons/${marathonId}/participants`,
                    params: {
                        page: params.page,
                        per_page: params.per_page,
                        status: params.status,
                        paid:
                            typeof params.paid === "boolean"
                                ? params.paid
                                    ? 1
                                    : 0
                                : undefined,
                        search: params.search,
                    },
                };
            },
            providesTags: (_result, _error, args) => [
                {
                    type: "AdminMarathonParticipants" as const,
                    id: args.marathonId,
                },
            ],
        }),
        getAdminMarathonTickets: builder.query<
            AdminMarathonTicketsResponse,
            AdminMarathonTicketsFilters
        >({
            query: ({ marathonId, ...rest }) => {
                const params = rest ?? {};

                return {
                    url: `/admin/marathons/${marathonId}/tickets`,
                    params: {
                        page: params.page,
                        per_page: params.per_page,
                        status: params.status,
                        only_deleted: params.only_deleted ? 1 : undefined,
                        search: params.search,
                    },
                };
            },
            providesTags: (_result, _error, args) => [
                {
                    type: "AdminMarathonTickets" as const,
                    id: args.marathonId,
                },
            ],
        }),
        getAdminMarathonBets: builder.query<
            AdminMarathonBetsResponse,
            AdminMarathonBetsFilters
        >({
            query: ({ marathonId, ...rest }) => {
                const params = rest ?? {};

                return {
                    url: `/admin/marathons/${marathonId}/bets`,
                    params: {
                        page: params.page,
                        per_page: params.per_page,
                        search: params.search,
                    },
                };
            },
            providesTags: (_result, _error, args) => [
                {
                    type: "AdminMarathonBets" as const,
                    id: args.marathonId,
                },
            ],
        }),
        getAdminMarathonStatistics: builder.query<
            AdminMarathonStatistics,
            number
        >({
            query: (marathonId) => `/admin/marathons/${marathonId}/statistics`,
            transformResponse: (
                response: AdminMarathonStatisticsResponse
            ) => response.data,
            providesTags: (_result, _error, marathonId) => [
                { type: "AdminMarathonStats" as const, id: marathonId },
            ],
        }),
        getAdminMarathonWinners: builder.query<
            AdminMarathonWinnersResponse,
            number
        >({
            query: (marathonId) => `/admin/marathons/${marathonId}/winners`,
            providesTags: (_result, _error, marathonId) => [
                { type: "AdminMarathonWinners" as const, id: marathonId },
            ],
        }),
        replaceMarathonParticipant: builder.mutation<
            { success: boolean; message: string },
            { marathonId: number; participantId: number; newUserId: number }
        >({
            query: ({ marathonId, participantId, newUserId }) => ({
                url: `/admin/marathons/${marathonId}/participants/${participantId}/replace`,
                method: "POST",
                body: { new_user_id: newUserId },
            }),
            invalidatesTags: (_result, _error, args) => [
                {
                    type: "AdminMarathonParticipants" as const,
                    id: args.marathonId,
                },
                { type: "AdminMarathons" as const, id: args.marathonId },
            ],
        }),
        transferMarathonParticipantToCompany: builder.mutation<
            { success: boolean; message: string },
            { marathonId: number; participantId: number }
        >({
            query: ({ marathonId, participantId }) => ({
                url: `/admin/marathons/${marathonId}/participants/${participantId}/transfer-to-company`,
                method: "POST",
            }),
            invalidatesTags: (_result, _error, args) => [
                {
                    type: "AdminMarathonParticipants" as const,
                    id: args.marathonId,
                },
                { type: "AdminMarathons" as const, id: args.marathonId },
            ],
        }),
        payMarathonParticipant: builder.mutation<
            { success: boolean; message: string },
            { marathonId: number; participantId: number }
        >({
            query: ({ marathonId, participantId }) => ({
                url: `/admin/marathons/${marathonId}/participants/${participantId}/pay`,
                method: "POST",
            }),
            invalidatesTags: (_result, _error, args) => [
                {
                    type: "AdminMarathonParticipants" as const,
                    id: args.marathonId,
                },
                { type: "AdminMarathons" as const, id: args.marathonId },
            ],
        }),
        previewMarathonPrize: builder.mutation<
            AdminMarathonAwardPreviewResponse,
            AwardMarathonPrizePayload
        >({
            query: (body) => ({
                url: "/admin/marathons/award-prize/preview",
                method: "POST",
                body,
            }),
        }),
        awardMarathonPrize: builder.mutation<
            { success: boolean; message: string },
            AwardMarathonPrizePayload
        >({
            query: (body) => ({
                url: "/admin/marathons/award-prize",
                method: "POST",
                body,
            }),
            invalidatesTags: (_result, _error, args) => [
                {
                    type: "AdminMarathonParticipants" as const,
                    id: args.marafone_id,
                },
                { type: "AdminMarathonWinners" as const, id: args.marafone_id },
                { type: "AdminMarathons" as const, id: args.marafone_id },
                { type: "AdminMarathons" as const, id: "LIST" },
                { type: "AdminMarathons" as const, id: "AWARDABLE" },
            ],
        }),
        getUserAccountsAndTickets: builder.query<
            AdminMarathonUserAccountsAndTicketsResponse,
            { marathonId: number; participantId: number; userId: number }
        >({
            query: ({ marathonId, participantId, userId }) => ({
                url: `/admin/marathons/${marathonId}/participants/${participantId}/user-accounts-and-tickets`,
                method: "GET",
                params: { user_id: userId },
            }),
            providesTags: (_result, _error, args) => [
                { type: "AdminMarathons" as const, id: args.marathonId },
            ],
        }),
        updateUserAccount: builder.mutation<
            { success: boolean; message: string },
            { accountId: number; data: any; marathonId: number }
        >({
            query: ({ accountId, data }) => ({
                url: `/admin/marathons/accounts/${accountId}`,
                method: "PUT",
                body: data,
            }),
            invalidatesTags: (_result, _error, args) => [
                { type: "AdminMarathons" as const, id: args.marathonId },
            ],
        }),
        updateUserTicket: builder.mutation<
            { success: boolean; message: string },
            { ticketId: number; data: any; marathonId: number }
        >({
            query: ({ ticketId, data }) => ({
                url: `/admin/marathons/tickets/${ticketId}`,
                method: "PUT",
                body: data,
            }),
            invalidatesTags: (_result, _error, args) => [
                { type: "AdminMarathons" as const, id: args.marathonId },
            ],
        }),
        }),
    overrideExisting: false,
});

export const {
    useGetAdminMarathonsQuery,
    useLazyGetAdminMarathonsQuery,
    useGetAdminMarathonQuery,
    useGetAwardableMarathonsQuery,
    useGetAdminMarathonParticipantsQuery,
    useGetAdminMarathonTicketsQuery,
    useGetAdminMarathonBetsQuery,
    useGetAdminMarathonStatisticsQuery,
    useGetAdminMarathonWinnersQuery,
    useReplaceMarathonParticipantMutation,
    useTransferMarathonParticipantToCompanyMutation,
    usePayMarathonParticipantMutation,
    usePreviewMarathonPrizeMutation,
    useAwardMarathonPrizeMutation,
    useLazyGetUserAccountsAndTicketsQuery,
    useGetUserAccountsAndTicketsQuery,
    useUpdateUserAccountMutation,
    useUpdateUserTicketMutation,
} = adminMarathonApi;
